import { useEffect, useMemo, useState } from 'react';
import {
    Col,
    Row,
    Typography,
    Modal,
    notification,
    Input,
    Space,
    Button,
    Tag,
    InputNumber,
    Tooltip,
} from 'antd';
import moment from 'moment';
import { RootStateOrAny, useSelector } from 'react-redux';

import { AiFillEdit } from 'react-icons/ai';
import CardDataBase from '../../../CardDataBase';
import { getWorkspaceStatus } from '../../utils/workspace';
import { EWorkspaceStatus, EWorkspaceStatusText } from '../../../../types/workspaces';
import { Plan } from '../../../../interfaces/plan';
import { editWorkspace } from '../../../../api/workspaces';
import PlanSelector from './PlanSelector';
import PaymentSelector from './PaymentSelector';
import { EDiscountType } from '../../../../enums/promotional.enum';
import ModularWorkspaceSelector from './ModularWorkspaceSwitch';

const { Text } = Typography;

interface IInformationDetail {
    createdAt: string;
    ownerEmail: string;
    currency: string;
    country: string;
    payment?: {
        paymentGateway?: string;
        paymentMethod?: string;
    };
    isDeleted: boolean;
    isSuspended: boolean;
    suspendedMessage: string;
    plan: string;
    workspaceId: string;
    deletedAt: string
    name: string;
    mrrBusinessValue: string;
    hasModularPlan: boolean;
    churnRisk: boolean;
    expireDiscountDate: Date;
    discountType: EDiscountType;
}

const InformationDetail = ({
    createdAt,
    ownerEmail,
    currency,
    country,
    payment,
    isDeleted,
    isSuspended,
    suspendedMessage,
    plan,
    workspaceId,
    deletedAt,
    name,
    mrrBusinessValue,
    hasModularPlan,
    churnRisk,
    expireDiscountDate,
    discountType,
}: IInformationDetail) => {
    const status = useMemo(
        () => getWorkspaceStatus({ isDeleted, isSuspended }),
        [isDeleted, isSuspended],
    );
    const { plans } = useSelector((state: RootStateOrAny) => state.plans);

    const [planActive, setPlanActive] = useState<Plan>();
    const [edit, setEdit] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [nameWS, setNameWS] = useState<string>(name);
    const [mrr, setMrr] = useState<any>(mrrBusinessValue);
    const [openCollectMRR, setOpenCollectMRR] = useState<boolean>(false);

    const returnTagStyle = (status: string) => {
        switch (status) {
        case EWorkspaceStatus.ACTIVE: return '#41971d';
        case EWorkspaceStatus.DELETED: return 'red';
        case EWorkspaceStatus.SUSPENDED: return 'yellow';
        case EWorkspaceStatus.DELETED_SUSPENDED: return 'red';
        default: return 'green';
        }
    };

    const confirmChangePlan = (plan: Plan) => Modal.confirm({
        title: 'Change plan',
        content: `Are you sure you want to change the plan to ${plan.name}?`,
        onOk: async () => {
            try {
                await editWorkspace(workspaceId, { accountingPlan: plan._id });
                setPlanActive(plan);
                notification.success({
                    message: 'Success',
                    description: 'Plan changed successfully',
                });
            } catch (error) {
                console.log(error);
                notification.error({
                    message: 'Error',
                    description: 'Something went wrong, please try again later',
                });
            }
        },
        okText: 'Change',
        cancelText: 'Cancel',
    });

    const finishEditing = () => {
        const payload = {
            name: nameWS,
            mrrBusinessValue: Number(mrr),
        };

        editWorkspace(workspaceId, payload)
            .then(({ data }) => {
                notification.success({
                    message: data.message,
                });
            })
            .catch((error) => {
                const description = error.response?.data?.message || 'An error has occurred';
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => {
                setNameWS(nameWS);
                setMrr(mrr);
                setEdit(null);
            });
    };

    const CARD_CONTENT = [
        {
            title: 'Name',
            content: (
                edit === 'NAME' ? (
                    <Input
                        value={nameWS}
                        onChange={(e: any) => setNameWS(e.target.value)}
                        onBlur={finishEditing}
                        onPressEnter={finishEditing}
                        autoFocus
                    />
                ) : (
                    <Space size={8}>
                        <Text>
                            {nameWS}
                        </Text>
                        <Button
                            type="link"
                            size="small"
                            icon={(
                                <AiFillEdit
                                    style={{ verticalAlign: 'middle' }}
                                    size={16}
                                />
                            )}
                            onClick={() => setEdit('NAME')}
                            style={{ padding: 0 }}
                        />
                    </Space>
                )
            ),
        },
        {
            title: 'Status',
            content: (
                status && (
                    <Space direction="vertical" size={8}>
                        <Tag color={returnTagStyle(status)}>
                            {EWorkspaceStatusText[status]}
                        </Tag>
                        {
                            isDeleted && (
                                <Tag color="red">
                                    {moment(deletedAt).format('HH:mm - DD MMM YYYY')}
                                </Tag>
                            )
                        }
                        {
                            (isSuspended && suspendedMessage) && (
                                <Text strong style={{ fontStyle: 'italic' }}>
                                    {suspendedMessage}
                                </Text>
                            )
                        }
                    </Space>
                )
            ),
        },
        {
            title: 'Created at',
            content: moment(createdAt).format('ddd DD MMM YYYY HH:mm'),
        },
        {
            title: 'Email',
            content: ownerEmail,
        },
        {
            title: 'Currency',
            content: currency,
        },
        {
            title: 'Country',
            content: country,
        },
        {
            title: 'Plan',
            content: (
                <PlanSelector
                    plan={planActive as Plan}
                    changePlan={confirmChangePlan}
                />
            ),
        },
        {
            title: 'Payment method',
            content: (
                <PaymentSelector
                    workspaceId={workspaceId}
                    payment={payment}
                />
            ),
        },
        {
            title: 'Gateway',
            content: (
                <Text>
                    {payment?.paymentGateway || 'Not registered'}
                </Text>
            ),
        },
        {
            title: 'Has modular plan',
            content: (
                <ModularWorkspaceSelector
                    workspaceId={workspaceId}
                    hasModularPlan={hasModularPlan}
                />
            ),
        },
        {
            title: 'Churn risk',
            content: (
                <Text>
                    {churnRisk ? 'Yes' : 'No'}
                </Text>
            ),
        },
        {
            title: 'Promotional discount',
            content: (
                discountType ? (
                    <Space size={4}>
                        <Text>
                            {discountType}
                        </Text>
                        <Tag color="blue">
                            {`Expires on ${moment(expireDiscountDate).format('DD MMM')}`}
                        </Tag>
                    </Space>
                ) : (
                    <Text>
                        No promotional discount
                    </Text>
                )
            ),
        },
        {
            title: 'MRR',
            content: (
                <Space size={12}>
                    {edit === 'MRR' ? (
                        <InputNumber
                            value={mrr}
                            onChange={(value: any) => setMrr(value)}
                            style={{ width: 130 }}
                            onPressEnter={finishEditing}
                            stringMode
                            controls={false}
                            prefix="$"
                            formatter={(value: any) => Number(value).toLocaleString('en-US', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                            })}
                            parser={(value: any) => value.replace(/\$\s?|,/g, '')}
                        />
                    ) : (
                        <Space size={8}>
                            <Text>
                                {mrr || '0.00'}
                            </Text>
                            <Button
                                type="link"
                                size="small"
                                icon={(
                                    <AiFillEdit
                                        style={{ verticalAlign: 'middle' }}
                                        size={16}
                                    />
                                )}
                                onClick={() => setEdit('MRR')}
                                style={{ padding: 0 }}
                            />
                        </Space>
                    )}
                </Space>

            ),
        },
    ];

    useEffect(() => {
        if (!plan) return;
        const activePlan = plans.find((p: Plan) => p._id === plan);
        if (!activePlan) return;
        setPlanActive(activePlan);
    }, [plan, plans]);

    useEffect(() => {
        setNameWS(name);
    }, [name]);

    return (
        <CardDataBase title="Information" style={{ minHeight: 345 }}>
            {
                CARD_CONTENT.map((data, index) => (
                    <Row
                        align="middle"
                        justify="space-between"
                        style={{ margin: '16px 0' }}
                        key={index}
                    >
                        <Col>
                            <Text strong>
                                {data.title}
                                :
                            </Text>
                        </Col>
                        <Col>{data.content}</Col>
                    </Row>
                ))
            }
        </CardDataBase>
    );
};

export default InformationDetail;
